import {
  Heading3,
  TextBody,
} from '@front/components/data-display/typography/Typography'
import { useScreenSize } from '@front/components/hooks/useScreenSize'
import { AppButton } from '@front/components/inputs/app-button'
import { IconButton } from '@front/components/inputs/icon-button'
import { CloseOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { Box, Dialog, Drawer, Stack, useTheme } from '@mui/material'

type DialogProps = {
  isOpen: boolean
  establishmentUri?: string
  regionUri?: string
  cityUri?: string
  handleClose: () => void
}

export default function MoreCareerInfoDialog({
  isOpen,
  handleClose,
  establishmentUri,
  regionUri,
  cityUri,
}: DialogProps) {
  const theme = useTheme()

  const { isSmallScreen } = useScreenSize()

  const goToLink = (uri: string) => {
    try {
      const url = new URL(uri)
      if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        return
      }
    } catch {
      return
    }
    window.open(uri, '_blank', 'noopener, noreferrer')
  }

  const Content = () => {
    return (
      <Stack
        sx={{
          gap: 1,
          px: 2,
          py: 1.75,
          overflow: 'hidden',
          borderRadius: '8px',
          backgroundColor: theme.palette.background.surface,
        }}
      >
        <Stack
          sx={{
            position: 'relative',
            width: { xs: '100%', md: '400px' },
          }}
        >
          <Heading3
            sx={{ textAlign: { xs: 'start', md: 'center' }, margin: 1 }}
          >
            Informations Pratiques {/**@TranslationNeeded */}
          </Heading3>
          <IconButton
            sx={{
              width: 24,
              height: 24,
              position: 'absolute',
              top: '10px',
              right: { xs: '8px', md: '16px' },
            }}
            onClick={handleClose}
          >
            <CloseOutlined sx={{ color: theme.palette.text.brand }} />
          </IconButton>
        </Stack>
        {establishmentUri && (
          <>
            <AppButton
              variant="text"
              sx={{ justifyContent: 'start' }}
              onClick={() => {
                goToLink(establishmentUri)
              }}
            >
              <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <OpenInNewOutlined
                  sx={{
                    width: 16,
                    height: 16,
                    color: theme.palette.text.primary,
                    my: '10px',
                    mr: '10px',
                  }}
                />
                <TextBody sx={{ color: theme.palette.text.primary }}>
                  Voir le site de l'établissement {/**@TranslationNeeded */}
                </TextBody>
              </Stack>
            </AppButton>
            <Stack sx={{ flexDirection: 'row' }}>
              <Box sx={{ width: 30, height: '1px' }} />
              <Box
                sx={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: theme.palette.action['primary-bg-disabled'],
                }}
              />
            </Stack>
          </>
        )}

        {regionUri && (
          <>
            <AppButton
              variant="text"
              sx={{ justifyContent: 'start' }}
              onClick={() => {
                goToLink(regionUri)
              }}
            >
              <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <OpenInNewOutlined
                  sx={{
                    width: 16,
                    height: 16,
                    color: theme.palette.text.primary,
                    my: '10px',
                    mr: '10px',
                  }}
                />
                <TextBody sx={{ color: theme.palette.text.primary }}>
                  Voir le site de la région {/**@TranslationNeeded */}
                </TextBody>
              </Stack>
            </AppButton>
            <Stack sx={{ flexDirection: 'row' }}>
              <Box sx={{ width: 30, height: '1px' }} />
              <Box
                sx={{
                  width: '100%',
                  height: '1px',
                  backgroundColor: theme.palette.action['primary-bg-disabled'],
                }}
              />
            </Stack>
          </>
        )}

        {cityUri && (
          <>
            <AppButton
              variant="text"
              sx={{ justifyContent: 'start' }}
              onClick={() => {
                goToLink(cityUri)
              }}
            >
              <Stack sx={{ flexDirection: 'row', alignItems: 'center' }}>
                <OpenInNewOutlined
                  sx={{
                    width: 16,
                    height: 16,
                    color: theme.palette.text.primary,
                    my: '10px',
                    mr: '10px',
                  }}
                />
                <TextBody sx={{ color: theme.palette.text.primary }}>
                  Voir le site de la ville {/**@TranslationNeeded */}
                </TextBody>
              </Stack>
            </AppButton>
          </>
        )}
      </Stack>
    )
  }

  if (isSmallScreen) {
    return (
      <Drawer
        anchor="bottom"
        open={isOpen}
        onClose={handleClose}
        slotProps={{
          paper: { sx: { borderRadius: '16px 16px 0 0' } },
        }}
      >
        <Content />
      </Drawer>
    )
  }

  return (
    <Dialog
      disableEnforceFocus
      open={isOpen}
      onClose={handleClose}
      PaperComponent={Content}
    />
  )
}
